import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../store/hooks";
import { 
  setModuleSupplyUnits, 
  addModuleAmmunition, 
  removeModuleAmmunition,
  updateModuleAmmunition
} from "../../../store/features/moduleSlice";
import { AMMO_TYPES } from "../../../constants/ship";
import { AmmunitionStorage } from "../../../types/ship";
import { formatCurrency } from "../../../utils/formatting";

/**
 * Module Cargo Step Component
 * Handles cargo capacity, supply units, and ammunition storage for modules
 */
export const ModuleCargoStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const moduleConfig = useAppSelector((state) => state.module.moduleConfig);
  const [daysOfSupply, setDaysOfSupply] = useState<number>(100);

  // Category and mount type mapping for ammo types
  const ammoCategoryMap: Record<string, { category: string; mount: string }> = React.useMemo(() => ({
    // Missiles
    basicMissile: { category: "Missile", mount: "Fixed/Turret" },
    advancedMissile: { category: "Missile", mount: "Fixed/Turret" },
    nuclearMissile: { category: "Missile", mount: "Fixed/Turret" },
    fragmentationMissile: { category: "Missile", mount: "Fixed/Turret" },
    decoyMissile: { category: "Missile", mount: "Fixed/Turret" },
    antiTorpedoMissile: { category: "Missile", mount: "Fixed/Turret" },
    ionMissile: { category: "Missile", mount: "Fixed/Turret" },
    jumpbreakerMissile: { category: "Missile", mount: "Fixed/Turret" },
    longRangeMissile: { category: "Missile", mount: "Fixed/Turret" },
    multiWarheadMissile: { category: "Missile", mount: "Fixed/Turret" },
    antimatterMissile: { category: "Missile", mount: "Fixed/Turret" },
    ortilleryMissile: { category: "Missile", mount: "Fixed/Turret" },
    shockwaveMissile: { category: "Missile", mount: "Fixed/Turret" },
    orbitalStrikeMissile: { category: "Orbital Strike", mount: "Fixed/Turret" },
    // Torpedoes
    basicTorpedo: { category: "Torpedo", mount: "Bay" },
    advancedTorpedoBarbette: { category: "Torpedo", mount: "Barbette" },
    advancedTorpedo: { category: "Torpedo", mount: "Bay" },
    standardTorpedo: { category: "Torpedo", mount: "Bay" },
    antimatterTorpedo: { category: "Torpedo", mount: "Bay" },
    antimatterBombPumpedTorpedo: { category: "Torpedo", mount: "Bay" },
    antiradiationTorpedo: { category: "Torpedo", mount: "Bay" },
    bombPumpedTorpedo: { category: "Torpedo", mount: "Bay" },
    ionTorpedo: { category: "Torpedo", mount: "Bay" },
    multiWarheadAntimatterTorpedo: { category: "Torpedo", mount: "Bay" },
    multiWarheadStandardTorpedo: { category: "Torpedo", mount: "Bay" },
    multiWarheadNuclearTorpedo: { category: "Torpedo", mount: "Bay" },
    nuclearTorpedo: { category: "Torpedo", mount: "Bay" },
    ortilleryTorpedo: { category: "Torpedo", mount: "Bay" },
    plasmaTorpedo: { category: "Torpedo", mount: "Bay" },
    // Sand Canisters
    basicSand: { category: "Sand Canister", mount: "Fixed/Turret" },
    advancedSand: { category: "Sand Canister", mount: "Fixed/Turret" },
    sandCanister: { category: "Sand Canister", mount: "Fixed/Turret" },
    antiPersonnelCanister: { category: "Sand Canister", mount: "Fixed/Turret" },
    chaffCanister: { category: "Sand Canister", mount: "Fixed/Turret" },
    pebbleCanister: { category: "Sand Canister", mount: "Fixed/Turret" },
    sandcutterCanister: { category: "Sand Canister", mount: "Fixed/Turret" },
    // Railgun
    railgunBarbetteAmmo: { category: "Railgun", mount: "Barbette" },
    railgunAmmo: { category: "Railgun", mount: "Bay" },
    railgunSpinalAmmo: { category: "Railgun", mount: "Spinal Mount" },
    railgunBaySmallAmmo: { category: "Railgun", mount: "Bay" },
    railgunBayMediumAmmo: { category: "Railgun", mount: "Bay" },
    railgunBayLargeAmmo: { category: "Railgun", mount: "Bay" },
    // Mass Driver
    massDriverSpinalAmmo: { category: "Mass Driver", mount: "Spinal Mount" },
    massDriverBaySmallAmmo: { category: "Mass Driver", mount: "Bay" },
    massDriverBayMediumAmmo: { category: "Mass Driver", mount: "Bay" },
    massDriverBayLargeAmmo: { category: "Mass Driver", mount: "Bay" },
  }), []);

  // List of categories for dropdown 1
  const weaponCategories = [
    "Missile",
    "Torpedo",
    "Sand Canister",
    "Railgun",
    "Mass Driver",
    "Orbital Strike",
  ];

  // List of mount types for dropdown 2
  const mountTypeLabels: Record<string, string> = {
    "Fixed/Turret": "Fixed/Turret",
    Barbette: "Barbette",
    Bay: "Bay",
    "Spinal Mount": "Spinal Mount",
  };

  // State for dropdowns
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedMount, setSelectedMount] = useState<string>("");
  const [selectedAmmoType, setSelectedAmmoType] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(1);

  // Filter ammo types by selected category
  const ammoTypesByCategory = React.useMemo(() => {
    return Object.entries(AMMO_TYPES).filter(([id, ammo]) => {
      const map = ammoCategoryMap[id];
      return map && map.category === selectedCategory;
    });
  }, [selectedCategory, ammoCategoryMap]);

  // Get unique mount types for selected category
  const mountTypesForCategory = React.useMemo(() => {
    const mounts = new Set<string>();
    ammoTypesByCategory.forEach(([id]) => {
      const map = ammoCategoryMap[id];
      if (map && map.mount) mounts.add(map.mount);
    });
    return Array.from(mounts);
  }, [ammoTypesByCategory, ammoCategoryMap]);

  // Filter ammo types by selected mount
  const filteredAmmoTypes = React.useMemo(() => {
    return ammoTypesByCategory.filter(([id]) => {
      const map = ammoCategoryMap[id];
      if (!selectedMount) return true;
      return map && map.mount === selectedMount;
    });
  }, [ammoTypesByCategory, selectedMount, ammoCategoryMap]);

  // When category changes, reset mount and ammo type
  React.useEffect(() => {
    setSelectedMount("");
    setSelectedAmmoType("");
  }, [selectedCategory]);

  // When mount changes, reset ammo type
  React.useEffect(() => {
    setSelectedAmmoType("");
  }, [selectedMount]);

  // When filtered ammo types change, auto-select the first available
  React.useEffect(() => {
    if (filteredAmmoTypes.length > 0) {
      setSelectedAmmoType(filteredAmmoTypes[0][0]);
    } else {
      setSelectedAmmoType("");
    }
  }, [filteredAmmoTypes]);

  if (!moduleConfig) {
    return <div className="text-gray-600 dark:text-gray-300">Loading module configuration...</div>;
  }

  // Calculate total tonnage used by all installed systems
  const calculateUsedTonnage = () => {
    let totalUsed = 0;
    
    // Core systems
    if (moduleConfig.systems?.bridge) totalUsed += moduleConfig.systems.bridge.tonnage || 0;
    if (moduleConfig.systems?.computer?.length) {
      moduleConfig.systems.computer.forEach(comp => {
        totalUsed += comp.tonnage || 0;
      });
    }
    if (moduleConfig.systems?.sensors) totalUsed += moduleConfig.systems.sensors.tonnage || 0;
    if (moduleConfig.systems?.powerPlant?.length) {
      moduleConfig.systems.powerPlant.forEach(plant => {
        totalUsed += plant.tonnage || 0;
      });
    }
    if (moduleConfig.systems?.drives?.length) {
      moduleConfig.systems.drives.forEach(drive => {
        totalUsed += drive.tonnage || 0;
      });
    }
    if (moduleConfig.systems?.fuel) totalUsed += moduleConfig.systems.fuel.tonnage || 0;
    if (moduleConfig.systems?.staterooms?.length) {
      moduleConfig.systems.staterooms.forEach(stateroom => {
        totalUsed += stateroom.tonnage || 0;
      });
    }
    if (moduleConfig.systems?.weapons?.length) {
      moduleConfig.systems.weapons.forEach(weapon => {
        totalUsed += weapon.tonnage || 0;
      });
    }
    
    // Optional systems
    if (moduleConfig.systems?.optionalSystems?.length) {
      moduleConfig.systems.optionalSystems.forEach(system => {
        totalUsed += system.tonnage || 0;
      });
    }
    
    return totalUsed;
  };

  const moduleSize = moduleConfig.size || 0;
  const usedTonnage = calculateUsedTonnage();
  const availableCargoSpace = Math.max(0, moduleSize - usedTonnage);

  // Supply Units calculations
  const supplyUnits = moduleConfig.supplyUnits || 0;
  const supplyTons = moduleConfig.supplyTons || 0;
  const dailyConsumption = Math.ceil(moduleSize / 100); // SU per day
  const recommendedSupplyTons = Math.ceil((dailyConsumption * 100) / 100); // 100 days worth
  const maximumSupplyTons = Math.max(0, Math.floor(availableCargoSpace)); // Ensure non-negative
  const supplyUnitsPerTon = 100; // 100 SU = 1 ton

  const handleSupplyUnitsChange = (units: number) => {
    const maxUnits = maximumSupplyTons * supplyUnitsPerTon;
    const clampedUnits = Math.max(0, Math.min(units, maxUnits));
    dispatch(setModuleSupplyUnits(clampedUnits));
    setDaysOfSupply(
      clampedUnits > 0 ? Math.ceil(clampedUnits / dailyConsumption) : 0,
    );
  };

  const handleDaysOfSupplyChange = (days: number) => {
    const units = days * dailyConsumption;
    const maxUnits = maximumSupplyTons * supplyUnitsPerTon;
    const clampedUnits = Math.max(0, Math.min(units, maxUnits));
    dispatch(setModuleSupplyUnits(clampedUnits));
    setDaysOfSupply(clampedUnits > 0 ? Math.ceil(clampedUnits / dailyConsumption) : 0);
  };

  // Ammunition management
  const ammunitionStorage = moduleConfig.systems?.ammunitionStorage || [];

  const handleAddAmmunition = () => {
    if (!selectedAmmoType || quantity <= 0) return;
    const ammoData = AMMO_TYPES[selectedAmmoType as keyof typeof AMMO_TYPES];
    if (!ammoData) return;
    const newAmmunition: AmmunitionStorage = {
      id: `${selectedAmmoType}-${Date.now()}`,
      ammoType: selectedAmmoType,
      quantity,
      name: ammoData.name,
      cost: ammoData.cost,
      tonsPerReload: ammoData.tonsPerReload,
      traits: [...ammoData.traits],
      damage: ammoData.damage,
      tl: ammoData.tl,
      perReload: ammoData.perReload,
    };
    dispatch(addModuleAmmunition(newAmmunition));
    setQuantity(1);
  };

  const handleRemoveAmmunition = (index: number) => {
    dispatch(removeModuleAmmunition(index));
  };

  const handleUpdateAmmunitionQuantity = (index: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      handleRemoveAmmunition(index);
      return;
    }

    const updatedAmmunition = { ...ammunitionStorage[index], quantity: newQuantity };
    dispatch(updateModuleAmmunition({ index, ammunition: updatedAmmunition }));
  };

  const calculateTotalAmmunitionCost = () => {
    return ammunitionStorage.reduce((total: number, ammo: any) => {
      return total + ammo.cost * ammo.quantity;
    }, 0);
  };

  const calculateTotalAmmunitionTons = () => {
    return ammunitionStorage.reduce((total: number, ammo: any) => {
      return total + ammo.tonsPerReload * ammo.quantity;
    }, 0);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">📦 Cargo Configuration</h3>
        
        {/* Cargo Space Summary */}
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
          <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-3">Available Cargo Space</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{moduleSize}</div>
              <div className="text-sm text-blue-700 dark:text-blue-300">Module Size (tons)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{usedTonnage}</div>
              <div className="text-sm text-orange-700 dark:text-orange-300">Used Space (tons)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{availableCargoSpace}</div>
              <div className="text-sm text-green-700 dark:text-green-300">Available Cargo (tons)</div>
            </div>
          </div>
        </div>

        {/* Supply Units */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6">
          <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Supply Units (SU)</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="p-3 rounded border bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700">
              <div className="font-medium text-gray-900 dark:text-gray-100">Daily Consumption</div>
              <div className="font-bold text-blue-700 dark:text-blue-300">{dailyConsumption} SU/day</div>
              <div className="text-gray-600 dark:text-gray-300">
                {moduleSize} tons ÷ 100
              </div>
            </div>
            <div className="p-3 rounded border bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700">
              <div className="font-medium text-gray-900 dark:text-gray-100">Recommended Supply</div>
              <div className="font-bold text-green-700 dark:text-green-300">{recommendedSupplyTons} tons</div>
              <div className="text-gray-600 dark:text-gray-300">
                100 days worth
              </div>
            </div>
            <div className={`p-3 rounded border ${supplyUnits > 0 ? "bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700" : "bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"}`}>
              <div className="font-medium text-gray-900 dark:text-gray-100">Cargo Space Used</div>
              <div className="font-bold text-blue-700 dark:text-blue-300">
                {supplyUnits > 0 ? "✓ Assigned" : "○ Unassigned"}
              </div>
              <div className="text-gray-600 dark:text-gray-300">
                {supplyTons.toFixed(1)}/{availableCargoSpace.toFixed(1)} tons
              </div>
            </div>
            <div className="p-3 rounded border bg-purple-50 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700">
              <div className="font-medium text-gray-900 dark:text-gray-100">Days of Supply</div>
              <div className="font-bold text-purple-700 dark:text-purple-300">{daysOfSupply} days</div>
              <div className="text-gray-600 dark:text-gray-300">
                {supplyUnits} SU ÷ {dailyConsumption} SU/day
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Supply Units (SU)
              </label>
              <input
                type="number"
                min="0"
                max={maximumSupplyTons * supplyUnitsPerTon}
                value={supplyUnits}
                onChange={(e) => handleSupplyUnitsChange(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Max: {maximumSupplyTons * supplyUnitsPerTon} SU ({maximumSupplyTons} tons)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Days of Supply
              </label>
              <input
                type="number"
                min="0"
                max={Math.floor(maximumSupplyTons * supplyUnitsPerTon / dailyConsumption)}
                value={daysOfSupply}
                onChange={(e) => handleDaysOfSupplyChange(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Max: {Math.floor(maximumSupplyTons * supplyUnitsPerTon / dailyConsumption)} days
              </p>
            </div>
          </div>
        </div>

        {/* Ammunition Storage */}
        <div className="border-t border-gray-200 dark:border-gray-700 my-6"></div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Ammunition Storage</h4>
          
          {/* Ammunition Selection Interface */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {/* Weapon Category Select */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Weapon Category
              </label>
              <select
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">Choose weapon category...</option>
                {weaponCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            {/* Mount Type Select (if relevant) */}
            {mountTypesForCategory.length > 1 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Mount Type
                </label>
                <select
                  value={selectedMount}
                  onChange={e => setSelectedMount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">Choose mount type...</option>
                  {mountTypesForCategory.map(type => (
                    <option key={type} value={type}>{mountTypeLabels[type] || type}</option>
                  ))}
                </select>
              </div>
            )}
            {/* Ammunition Type Select */}
            <div className={mountTypesForCategory.length > 1 ? "md:col-span-2" : "md:col-span-3"}>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Ammunition Type
              </label>
              <select
                value={selectedAmmoType}
                onChange={e => setSelectedAmmoType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                disabled={filteredAmmoTypes.length === 0}
              >
                <option value="">Choose ammunition type...</option>
                {filteredAmmoTypes.map(([id, ammo]) => (
                  <option key={id} value={id}>
                    {ammo.name} (TL{ammo.tl}) - {ammo.damage} - {ammo.traits.join(", ")}
                  </option>
                ))}
              </select>
            </div>
            {/* Quantity and Add Button */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Quantity
              </label>
              <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  className="px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  -
                </button>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={quantity}
                  onChange={(e) =>
                    setQuantity(Math.max(1, parseInt(e.target.value) || 1))
                  }
                  className="w-full text-center border-0 focus:ring-0 focus:outline-none dark:bg-gray-700 dark:text-gray-100"
                />
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                >
                  +
                </button>
              </div>
            </div>
            <div>
              <button
                onClick={handleAddAmmunition}
                disabled={!selectedAmmoType}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-10"
              >
                Add Ammunition
              </button>
            </div>
          </div>

          {/* Stored Ammunition */}
          {ammunitionStorage.length > 0 && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700 my-4"></div>
              <h5 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-4">
                Stored Ammunition ({ammunitionStorage.length} types)
              </h5>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Ammunition
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Cost per Unit
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Total Cost
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Tons per Unit
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Total Tons
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {ammunitionStorage.map((ammo: any, index: number) => (
                      <tr key={ammo.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-3 py-2">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                              {ammo.name}
                              {typeof ammo.perReload === 'number' && ammo.perReload > 1 && (
                                <span className="ml-1 px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-[10px] font-semibold whitespace-nowrap">
                                  {ammo.perReload} per unit
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              TL{ammo.tl} • {ammo.damage} • {ammo.traits.join(", ")}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end">
                            <button
                              onClick={() => handleUpdateAmmunitionQuantity(index, ammo.quantity - 1)}
                              className="p-1 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                            >
                              -
                            </button>
                            <span className="mx-2 min-w-[30px] text-center text-sm text-gray-900 dark:text-gray-100">
                              {ammo.quantity}
                            </span>
                            <button
                              onClick={() => handleUpdateAmmunitionQuantity(index, ammo.quantity + 1)}
                              className="p-1 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                            >
                              +
                            </button>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                          {formatCurrency(ammo.cost)}
                        </td>
                        <td className="px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                          {formatCurrency(ammo.cost * ammo.quantity)}
                        </td>
                        <td className="px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                          {ammo.tonsPerReload.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                          {(ammo.tonsPerReload * ammo.quantity).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => handleRemoveAmmunition(index)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 p-1"
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 dark:bg-gray-700">
                      <td className="px-3 py-2">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Totals
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {ammunitionStorage.reduce(
                            (total: number, ammo: any) => total + ammo.quantity,
                            0,
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-sm text-gray-500 dark:text-gray-400">
                        —
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {formatCurrency(calculateTotalAmmunitionCost())}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-sm text-gray-500 dark:text-gray-400">
                        —
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {calculateTotalAmmunitionTons().toFixed(2)}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                        —
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
